import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/argus/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Plus, FolderGit2, Globe, FileCode2, Package, Cloud, ArrowRight, AlertTriangle, Download, Trash2 } from "lucide-react";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import SeverityChip from "../../components/argus/SeverityChip";

const TYPES = [
  { key: "dast", label: "Web App", Icon: Globe, desc: "DAST — probe a live web app you own. Domain verification required." },
  { key: "sast", label: "Source Code", Icon: FileCode2, desc: "SAST — paste a code file or upload a snippet. ~15 rules." },
  { key: "sca", label: "Dependencies", Icon: Package, desc: "SCA — paste a package.json or requirements.txt. CVE-matched." },
];

function NewProjectWizard({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [t, setT] = useState("sca");
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [manifest, setManifest] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [dastEngine, setDastEngine] = useState("builtin");
  const [dastProfile, setDastProfile] = useState("standard");
  const [sastRulePack, setSastRulePack] = useState("standard");
  const [gitUrl, setGitUrl] = useState("");
  const [sastSource, setSastSource] = useState("paste"); // "paste" | "git"
  const [targetKind, setTargetKind] = useState("code"); // "code" | "image" — only used for SCA
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep(1); setT("sca"); setName(""); setTarget(""); setManifest("");
    setAuthorized(false); setDastEngine("builtin"); setDastProfile("standard");
    setSastRulePack("standard"); setGitUrl(""); setSastSource("paste"); setTargetKind("code");
  };

  const create = async () => {
    setBusy(true);
    try {
      const payload = {
        name,
        type: t,
        target: t === "sast" && sastSource === "git" ? gitUrl : target,
        target_kind: t === "sca" ? targetKind : "code",
        description: "",
        manifest_content: t === "sca" && targetKind === "image" ? null : (t === "sast" && sastSource === "git" ? null : (manifest || null)),
        authorized: t === "dast" ? authorized : true,
        dast_engine: t === "dast" ? dastEngine : "builtin",
        dast_profile: t === "dast" ? dastProfile : "standard",
        sast_rule_pack: t === "sast" ? sastRulePack : "standard",
        git_url: t === "sast" && sastSource === "git" ? gitUrl : null,
      };
      const p = await api.post("/projects", payload);
      toast.success("Project created.");
      onCreated?.(p.data);
      setOpen(false);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the project.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button className="bg-gold text-paper hover:bg-gold2 h-10 font-semibold" data-testid="new-project-button">
          <Plus size={16} className="mr-2" /> New Project
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="new-project-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {step === 1 ? "What are you scanning?" : step === 2 ? "Tell us about it." : "Confirm and scan."}
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 text-xs text-ink/50 mb-2">
          <span className={step >= 1 ? "text-ink font-semibold" : ""}>1. Type</span>
          <span>·</span>
          <span className={step >= 2 ? "text-ink font-semibold" : ""}>2. Details</span>
          <span>·</span>
          <span className={step >= 3 ? "text-ink font-semibold" : ""}>3. Confirm</span>
        </div>

        {step === 1 && (
          <div className="grid sm:grid-cols-3 gap-3" data-testid="wizard-step-1">
            {TYPES.map((opt) => {
              const Icon = opt.Icon;
              const selected = t === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setT(opt.key)}
                  className={`text-left rounded-lg border p-4 transition-colors ${selected ? "border-ink bg-raised/40" : "border-line hover:bg-raised"}`}
                  data-testid={`wizard-type-${opt.key}`}
                >
                  <Icon size={22} className="text-gold mb-2" />
                  <div className="font-semibold">{opt.label}</div>
                  <div className="text-xs text-ink/60 mt-1">{opt.desc}</div>
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="wizard-step-2">
            <div>
              <Label htmlFor="pname">Project name</Label>
              <Input id="pname" value={name} onChange={(e) => setName(e.target.value)} className="h-12 mt-1" placeholder="My API service" data-testid="wizard-name" />
            </div>
            {t === "dast" && (
              <>
                <div>
                  <Label htmlFor="ptarget">Target URL</Label>
                  <Input id="ptarget" value={target} onChange={(e) => setTarget(e.target.value)} className="h-12 mt-1 font-mono" placeholder="https://app.yoursite.com" data-testid="wizard-target" />
                </div>
                <div className="rounded-md bg-sev-medium/10 border border-sev-medium/30 text-sm p-3 flex gap-2" data-testid="wizard-dast-warning">
                  <AlertTriangle size={16} className="text-sev-medium mt-0.5 shrink-0" />
                  <span>You'll verify domain ownership before any scan starts. Private IPs and metadata endpoints are blocked.</span>
                </div>
              </>
            )}
            {t === "sca" && (
              <div className="space-y-3">
                <div className="rounded-md border border-line bg-paper/40 p-3 flex flex-wrap gap-3" data-testid="wizard-sca-source-toggle">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="sca-source" value="manifest" checked={targetKind === "code"} onChange={() => setTargetKind("code")} data-testid="sca-source-manifest" />
                    <span>Package manifest</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="sca-source" value="image" checked={targetKind === "image"} onChange={() => setTargetKind("image")} data-testid="sca-source-image" />
                    <span>Container image (Docker / OCI)</span>
                  </label>
                </div>
                {targetKind === "code" ? (
                  <div>
                    <Label htmlFor="pman">Paste package.json or requirements.txt</Label>
                    <Textarea id="pman" value={manifest} onChange={(e) => setManifest(e.target.value)} className="min-h-[180px] font-mono text-xs mt-1" placeholder='{ "dependencies": { "lodash": "4.17.20" } }' data-testid="wizard-manifest" />
                    <Input value={target} onChange={(e) => setTarget(e.target.value)} className="h-10 mt-2 font-mono" placeholder="filename (e.g. package.json)" data-testid="wizard-target" />
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="pimg">Image reference</Label>
                    <Input id="pimg" value={target} onChange={(e) => setTarget(e.target.value)} className="h-12 mt-1 font-mono" placeholder="nginx:1.21-alpine, ghcr.io/owner/img:tag, …" data-testid="wizard-image-ref" />
                    <p className="text-xs text-ink/55 mt-2">
                      Public images only. ArgusSecure pulls the image anonymously, runs Trivy across every layer
                      (base + app), reports CVEs by package, then discards the local copy.
                    </p>
                  </div>
                )}
              </div>
            )}
            {t === "sast" && (
              <div className="space-y-3">
                <div className="rounded-md border border-line bg-paper/40 p-3 flex flex-wrap gap-3" data-testid="wizard-sast-source-toggle">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="sast-source" value="paste" checked={sastSource === "paste"} onChange={() => setSastSource("paste")} data-testid="sast-source-paste" />
                    <span>Paste a snippet</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="sast-source" value="git" checked={sastSource === "git"} onChange={() => setSastSource("git")} data-testid="sast-source-git" />
                    <span>Public Git repository</span>
                  </label>
                </div>

                {sastSource === "paste" ? (
                  <div>
                    <Label htmlFor="psrc">Paste source code (.py, .js, .ts, .go, .java)</Label>
                    <Textarea id="psrc" value={manifest} onChange={(e) => setManifest(e.target.value)} className="min-h-[200px] font-mono text-xs mt-1" placeholder="def hello(): ..." data-testid="wizard-source" />
                    <Input value={target} onChange={(e) => setTarget(e.target.value)} className="h-10 mt-2 font-mono" placeholder="filename (e.g. app.py)" data-testid="wizard-target" />
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="pgit">Public Git URL</Label>
                    <Input
                      id="pgit"
                      value={gitUrl}
                      onChange={(e) => setGitUrl(e.target.value)}
                      className="h-12 mt-1 font-mono"
                      placeholder="https://github.com/owner/repo"
                      data-testid="wizard-git-url"
                    />
                    <p className="text-xs text-ink/55 mt-2">
                      Public repositories only — github.com, gitlab.com, bitbucket.org.
                      ArgusSecure clones with <code>--depth 1</code>, scans the working tree, then deletes it.
                    </p>
                  </div>
                )}

                <div>
                  <Label htmlFor="psastpack">Rule pack</Label>
                  <select
                    id="psastpack"
                    value={sastRulePack}
                    onChange={(e) => setSastRulePack(e.target.value)}
                    className="h-10 mt-1 w-full rounded-md border border-line bg-paper/40 px-3 text-sm font-mono"
                    data-testid="wizard-sast-rule-pack"
                  >
                    <option value="standard">Standard — everything (recommended)</option>
                    <option value="owasp">OWASP Top 10:2025 — OWASP-mapped rules only</option>
                    <option value="secrets">Secrets — credentials &amp; keys only</option>
                    <option value="auth">Auth — authentication-class issues only</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="wizard-step-3">
            <div className="rounded-md bg-raised p-4 text-sm">
              <div><strong>Type:</strong> {TYPES.find((x) => x.key === t)?.label}</div>
              <div><strong>Name:</strong> {name || "(no name)"}</div>
              <div><strong>Target:</strong> <span className="font-mono">{target || "—"}</span></div>
            </div>
            {t === "dast" && (
              <label className="flex items-start gap-3 text-sm" data-testid="wizard-aup-row">
                <Checkbox checked={authorized} onCheckedChange={(v) => setAuthorized(!!v)} data-testid="wizard-aup" />
                <span>
                  I am authorized to test this target. I understand unauthorized scanning may violate the Computer Fraud and Abuse Act (US) and equivalent laws elsewhere.
                </span>
              </label>
            )}
            {t === "dast" && (
              <div className="rounded-md border border-line bg-paper/40 p-4 space-y-3" data-testid="wizard-engine-row">
                <div className="text-xs uppercase tracking-wider text-ink/50">DAST engine</div>
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <input type="radio" name="dast-engine" value="builtin" checked={dastEngine === "builtin"} onChange={() => setDastEngine("builtin")} data-testid="engine-builtin" />
                  <span>
                    <strong>Built-in</strong> — ArgusSecure's bundled scanner. No setup, runs immediately, covers the common OWASP attack classes.
                  </span>
                </label>
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <input type="radio" name="dast-engine" value="zap" checked={dastEngine === "zap"} onChange={() => setDastEngine("zap")} data-testid="engine-zap" />
                  <span>
                    <strong>OWASP ZAP</strong> — Drive scans through your own ZAP daemon for full enterprise coverage. Requires a configured ZAP integration on the <span className="text-gold">Integrations</span> page.
                  </span>
                </label>
              </div>
            )}
            {t === "dast" && dastEngine === "builtin" && (
              <div className="rounded-md border border-line bg-paper/40 p-4 space-y-3" data-testid="wizard-profile-row">
                <div className="text-xs uppercase tracking-wider text-ink/50">Scan profile</div>
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <input type="radio" name="dast-profile" value="passive" checked={dastProfile === "passive"} onChange={() => setDastProfile("passive")} data-testid="profile-passive" />
                  <span>
                    <strong>Passive</strong> — Header + cookie checks only. Single request. Safest possible.
                  </span>
                </label>
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <input type="radio" name="dast-profile" value="standard" checked={dastProfile === "standard"} onChange={() => setDastProfile("standard")} data-testid="profile-standard" />
                  <span>
                    <strong>Standard</strong> — Passive checks plus sensitive-file lookup, reflected-XSS / open-redirect / SQLi probes. <em>Default.</em>
                  </span>
                </label>
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <input type="radio" name="dast-profile" value="deep" checked={dastProfile === "deep"} onChange={() => setDastProfile("deep")} data-testid="profile-deep" />
                  <span>
                    <strong>Deep</strong> — Standard plus path-traversal probe and a 3× wider crawl. Slower; only run against your own staging.
                  </span>
                </label>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="wizard-back">Back</Button>}
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)} disabled={step === 2 && !name} className="bg-gold text-paper hover:bg-gold2" data-testid="wizard-next">
              Continue <ArrowRight size={16} className="ml-2" />
            </Button>
          ) : (
            <Button onClick={create} disabled={busy || (t === "dast" && !authorized)} className="bg-gold text-paper hover:bg-gold2" data-testid="wizard-create">
              {busy ? "Creating…" : "Create project"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [verifiedFilter, setVerifiedFilter] = useState("all");

  const refresh = () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (typeFilter !== "all") params.type = typeFilter;
    if (verifiedFilter === "verified") params.verified = true;
    if (verifiedFilter === "unverified") params.verified = false;
    setLoading(true);
    return api.get("/projects", { params }).then((r) => { setProjects(r.data); setLoading(false); });
  };

  useEffect(() => {
    const id = setTimeout(refresh, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, typeFilter, verifiedFilter]);

  const hasFilters = q || typeFilter !== "all" || verifiedFilter !== "all";

  const handleDelete = async (e, p) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`Delete "${p.name}" and all its findings? This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success(`"${p.name}" deleted.`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete the project.");
    }
  };

  const handleBackup = async (e, p) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const token = localStorage.getItem("argus.token");
      const r = await fetch(`${API}/projects/${p.id}/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const cd = r.headers.get("content-disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `argus-backup-${p.id.slice(0, 8)}.json`;
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = filename; a.click();
      URL.revokeObjectURL(u);
      toast.success(`Backup downloaded for "${p.name}".`);
    } catch (err) {
      toast.error(err?.message || "Backup failed.");
    }
  };

  return (
    <AppShell title="Projects" actions={<NewProjectWizard onCreated={refresh} />}>
      <div className="flex flex-wrap gap-3 mb-5" data-testid="projects-filters">
        <Input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search projects…"
          className="h-10 max-w-xs bg-paper border-line"
          data-testid="projects-search"
        />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-10 w-40 bg-paper border-line" data-testid="projects-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="sast">SAST</SelectItem>
            <SelectItem value="dast">DAST</SelectItem>
            <SelectItem value="sca">SCA</SelectItem>
            <SelectItem value="cloud">Cloud</SelectItem>
          </SelectContent>
        </Select>
        <Select value={verifiedFilter} onValueChange={setVerifiedFilter}>
          <SelectTrigger className="h-10 w-44 bg-paper border-line" data-testid="projects-verified-filter"><SelectValue placeholder="Verification" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="verified">Domain verified</SelectItem>
            <SelectItem value="unverified">Awaiting verification</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button variant="outline" onClick={() => { setQ(""); setTypeFilter("all"); setVerifiedFilter("all"); }}
            className="h-10 border-line bg-transparent text-ink/70" data-testid="projects-clear-filters">Clear</Button>
        )}
      </div>
      {loading ? (
        <div className="text-ink/50">Loading your projects…</div>
      ) : projects.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line p-12 text-center" data-testid="projects-empty">
          <FolderGit2 size={28} className="mx-auto text-ink/40 mb-3" />
          <p className="text-ink/70">{hasFilters ? "No projects match these filters." : "No projects yet. Start your first scan in three clicks."}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
          {projects.map((p) => {
            const c = p.open_findings_count || {};
            const TypeIcon = p.type === "cloud" ? Cloud : p.type === "dast" ? Globe : p.type === "sast" ? FileCode2 : Package;
            const isDemoCloud = p.type === "cloud" && p.target === "aws:account/111122223333";
            return (
              <Link
                key={p.id}
                to={`/app/projects/${p.id}`}
                className="rounded-xl border border-line bg-panel p-5 hover:shadow-md transition-shadow"
                data-testid={`project-card-${p.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-gold font-semibold">
                    <TypeIcon size={12} /> {p.type}
                  </span>
                  {p.type === "dast" && !p.domain_verified && (
                    <span className="sev-chip sev-medium">Verify Domain</span>
                  )}
                  {isDemoCloud && (
                    <span
                      className="text-[10px] uppercase tracking-wider border border-gold/30 bg-gold/10 text-gold rounded-full px-2 py-0.5"
                      title="Synthetic demo — connect AWS in /app/integrations to scan your own account."
                      data-testid="synthetic-demo-pill"
                    >
                      Synthetic demo
                    </span>
                  )}
                </div>
                <h3 className="font-display text-xl mb-1 line-clamp-2">{p.name}</h3>
                <div className="text-xs text-ink/50 font-mono truncate">{p.target || "—"}</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["critical", "high", "medium", "low"].filter((k) => c[k] > 0).map((k) => (
                    <span key={k} className={`sev-chip sev-${k}`}>{c[k]} {k}</span>
                  ))}
                  {Object.values(c).every((v) => !v) && <SeverityChip severity="info" />}
                </div>
                <div className="mt-4 flex items-center justify-between text-xs text-ink/50">
                  <span>Risk score</span>
                  <span className="font-mono text-ink">{(p.risk_score || 0).toFixed(1)}</span>
                </div>
                <div className="mt-4 pt-3 border-t border-line/60 flex items-center justify-end gap-1">
                  <button
                    onClick={(e) => handleBackup(e, p)}
                    title="Download backup (JSON)"
                    className="inline-flex items-center gap-1 text-xs text-ink/55 hover:text-gold px-2 py-1 rounded transition-colors"
                    data-testid={`project-backup-${p.id}`}
                  >
                    <Download size={12} /> Backup
                  </button>
                  <button
                    onClick={(e) => handleDelete(e, p)}
                    title="Delete project"
                    className="inline-flex items-center gap-1 text-xs text-ink/55 hover:text-sev-critical px-2 py-1 rounded transition-colors"
                    data-testid={`project-delete-${p.id}`}
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
